import { FloatingContext, FloatingFocusManager, FloatingPortal } from '@floating-ui/react'
import { motion } from 'framer-motion'
import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { SearchInput } from '../../components/SearchInput'
import { useTranslation } from 'react-i18next'
import { useVirtualList } from '@root/hooks'
import { TagPill } from '../../components/TagPill'
import { AnimatedContentLoader } from '../../components/AnimatedContentLoader'
import { FaCheck } from 'react-icons/fa'
import type { IconType } from 'react-icons'

const MAX_HEIGHT_DROPDOWN = 300
const ITEM_HEIGHT = 38

const foldDiacritics = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

export interface SelectableTag<TId extends string | number = number> {
  id: TId
  name: string
  description?: string
  color?: string
  icon?: IconType
}

interface TagSelectorDropdownProps<TId extends string | number = number> {
  context: FloatingContext
  refs: FloatingContext['refs']
  getFloatingProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>
  items: SelectableTag<TId>[]
  searchPlaceholder?: string
  onTagSelect?: (tagId: TId) => void
  isSelected?: (tag: SelectableTag<TId>) => boolean
  isLoading?: boolean
  onScrollBottom?: () => void
  /** When set, search is forwarded to the parent (e.g. API) instead of filtering locally. */
  onSearchChange?: (search: string) => void
  /** Optional control next to the search field. */
  action?: React.ReactNode
  /**
   * Optional trailing control per row. When provided, Tab toggles focus onto it and
   * Enter activates `onItemAction` for the focused row.
   */
  renderItemAction?: (tag: SelectableTag<TId>, focused: boolean) => React.ReactNode
  onItemAction?: (tag: SelectableTag<TId>) => void
}

export function TagSelectorDropDown<TId extends string | number = number>({
  context,
  refs,
  getFloatingProps,
  items,
  searchPlaceholder,
  onTagSelect,
  isSelected,
  isLoading = false,
  onScrollBottom,
  onSearchChange,
  action,
  renderItemAction,
  onItemAction
}: TagSelectorDropdownProps<TId>) {
  const [search, setSearch] = useState('')
  const [focusIndex, setFocusIndex] = useState(-1)
  const [isItemActionFocused, setIsItemActionFocused] = useState(false)
  const rootRef = useRef<HTMLElement>(document.getElementById('dropdown-root'))
  const [t] = useTranslation()

  const canFocusItemAction = Boolean(renderItemAction && onItemAction)
  const needle = foldDiacritics(search.trim())
  const tags =
    onSearchChange || !needle
      ? items
      : items.filter(
          tag =>
            foldDiacritics(tag.name).includes(needle) ||
            foldDiacritics(tag.description ?? '').includes(needle)
        )
  const allItemsLength = tags.length

  const { scrollRef, renderItems, scrollToIndex } = useVirtualList({
    items: tags,
    itemSize: ITEM_HEIGHT,
    onScrollBottom
  })

  const handleTagSelect = (tag: SelectableTag<TId>) => {
    onTagSelect?.(tag.id)
  }

  const handleSearch = (next: string) => {
    setSearch(next)
    onSearchChange?.(next)
  }

  const handleArrowDown = () => {
    if (isItemActionFocused) return

    if (focusIndex === allItemsLength - 1) {
      setFocusIndex(0)
      scrollToIndex(0)
      return
    }

    const newIndex = Math.min(focusIndex + 1, allItemsLength - 1)
    setFocusIndex(newIndex)
    scrollToIndex(newIndex)
  }

  const handleArrowUp = () => {
    if (isItemActionFocused) return

    if (focusIndex === 0) {
      setFocusIndex(allItemsLength - 1)
      scrollToIndex(allItemsLength - 1)
      return
    }

    const newIndex = Math.max(focusIndex - 1, 0)
    setFocusIndex(newIndex)
    scrollToIndex(newIndex)
  }

  const handleTab = e => {
    if (!canFocusItemAction) return

    if (focusIndex >= 0 && focusIndex < allItemsLength) {
      e.preventDefault()
      setIsItemActionFocused(!isItemActionFocused)
    }
  }

  const handleEnter = e => {
    e.stopPropagation()
    e.preventDefault()

    const tag = tags[focusIndex]
    if (!tag) return

    if (isItemActionFocused) {
      onItemAction?.(tag)
    } else {
      handleTagSelect(tag)
    }
  }

  const onKeyDown = e => {
    switch (e.key) {
      case 'ArrowDown':
        handleArrowDown()
        break
      case 'ArrowUp':
        handleArrowUp()
        break
      case 'Tab':
        handleTab(e)
        break
      case 'Enter':
        handleEnter(e)
        break
      default:
        break
    }
  }

  return (
    <FloatingPortal root={rootRef}>
      <FloatingFocusManager
        context={context}
        returnFocus={false}
        modal={false}
        order={['reference', 'content']}
      >
        <StyledDropdown
          ref={refs.setFloating}
          style={{
            top: context.y || 0,
            left: context.x || 0,
            position: context.strategy
          }}
          {...getFloatingProps()}
        >
          <StyledActionBar>
            <SearchInput
              placeholder={searchPlaceholder ?? t('components.tagSelector.searchPlaceholder')}
              onSearch={handleSearch}
              onKeyDown={onKeyDown}
              autoFocus={true}
            ></SearchInput>
            {action}
          </StyledActionBar>

          <StyledScrollableList ref={scrollRef}>
            <AnimatedContentLoader
              isLoading={isLoading}
              isEmpty={tags.length === 0}
              isEmptyDescription={t('components.tagSelector.noResults')}
            >
              {renderItems((tag, style, index) => (
                <StyledItemWrapper
                  className={`${focusIndex === index ? 'keyboard-focused' : ''} ${
                    focusIndex === index && isItemActionFocused ? 'item-action-focused' : ''
                  }`}
                  key={tag.id}
                  style={{ height: `${ITEM_HEIGHT}px`, ...style }}
                  onKeyDown={onKeyDown}
                  onClick={() => handleTagSelect(tag)}
                >
                  <StyledItem>
                    <StyledItemColumn>
                      <TagPill
                        tag={tag}
                        icon={tag.icon}
                        maxWidth={'100%'}
                        disableHoverText={true}
                      />
                    </StyledItemColumn>
                    <StyledRightItemsWrapper>
                      {renderItemAction?.(tag, isItemActionFocused && focusIndex === index)}
                      {isSelected?.(tag) && (
                        <StyledItemColumn>
                          <StyledCheckMarkWrapper>
                            <FaCheck />
                          </StyledCheckMarkWrapper>
                        </StyledItemColumn>
                      )}
                    </StyledRightItemsWrapper>
                  </StyledItem>
                </StyledItemWrapper>
              ))}
            </AnimatedContentLoader>
          </StyledScrollableList>
        </StyledDropdown>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}

export default TagSelectorDropDown

/** Hover/focus visibility for trailing row actions supplied via `renderItemAction`. */
export const TagSelectorItemAction = styled.div`
  display: none;
  align-self: center;

  &.focused {
    display: block;
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  }
`

const StyledDropdown = styled(motion.div).attrs({
  initial: { y: 10, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  exit: { y: 0, opacity: 0 }
})`
  display: flex;
  flex-direction: column;
  max-height: ${MAX_HEIGHT_DROPDOWN}px;
  background: white;
  z-index: 9999;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
`

const StyledActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledScrollableList = styled.div`
  overflow: auto;
  flex: 1;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledItemWrapper = styled.div`
  display: flex;
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  align-items: center;

  &.keyboard-focused ${TagSelectorItemAction}, &:hover ${TagSelectorItemAction} {
    display: block;
  }
  &.keyboard-focused,
  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }

  &.item-action-focused ${TagSelectorItemAction} {
    display: block;
  }
`

const StyledItem = styled(motion.div).attrs({
  variants: {
    hidden: {
      y: 5,
      opacity: 0
    },
    show: {
      y: 0,
      opacity: 1
    }
  }
})`
  display: flex;
  width: 100%;
  padding: 0.4rem 0.6rem;

  cursor: pointer;
  height: 35px;
`
const StyledItemColumn = styled.div`
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledRightItemsWrapper = styled.div`
  margin-left: auto;
  display: flex;
`

const StyledCheckMarkWrapper = styled.div`
  width: 1.2rem;
  height: 1.2rem;
  margin: ${({ theme }) => theme.spacing.sm}rem;

  svg {
    fill: ${({ theme }) => theme.colors.success};
  }
`
